// ── ManifestScopeTree — Tree renderer for manifest scopes ──────────────────
import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import type { ManifestScope, ScopeManifest } from '../types';
import { manifestScopeDisplay, decodeScopeKey } from '../utils';

interface ManifestScopeTreeProps {
  scopes: ManifestScope[];
  outcomes: ScopeManifest['outcomes'] & {};
  selectedKeys?: Set<string>;
  onToggleKey?: (key: string) => void;
  onToggleNamespace?: (ns: string, selected: boolean) => void;
}

export function ManifestScopeTree({
  scopes,
  outcomes,
  selectedKeys,
  onToggleKey,
  onToggleNamespace,
}: ManifestScopeTreeProps) {
  const selectable = !!(selectedKeys && onToggleKey);

  const { roots, childrenMap, outcomeCounts, nsByNs } = useMemo(() => {
    const childrenMap = new Map<string, ManifestScope[]>();
    const roots: ManifestScope[] = [];

    for (const s of scopes) {
      if (!s.parent_ref) {
        roots.push(s);
      } else {
        const pk = `${s.parent_ref.scope_namespace}\x00${s.parent_ref.scope_key}`;
        const arr = childrenMap.get(pk) ?? [];
        arr.push(s);
        childrenMap.set(pk, arr);
      }
    }

    const outcomeCounts = new Map<string, number>();
    for (const o of outcomes) {
      const k = `${o.scope_ref.scope_namespace}\x00${o.scope_ref.scope_key}`;
      outcomeCounts.set(k, (outcomeCounts.get(k) ?? 0) + 1);
    }

    const nsByNs = new Map<string, string[]>();
    for (const s of scopes) {
      const nk = `${s.scope_namespace}\x00${s.scope_key}`;
      const arr = nsByNs.get(s.scope_namespace) ?? [];
      arr.push(nk);
      nsByNs.set(s.scope_namespace, arr);
    }

    return { roots, childrenMap, outcomeCounts, nsByNs };
  }, [scopes, outcomes]);

  const groupedRoots = useMemo(() => {
    const groups = new Map<string, ManifestScope[]>();
    for (const r of roots) {
      const arr = groups.get(r.scope_namespace) ?? [];
      arr.push(r);
      groups.set(r.scope_namespace, arr);
    }
    return groups;
  }, [roots]);

  function renderNode(s: ManifestScope, depth: number): React.ReactNode {
    const nk = `${s.scope_namespace}\x00${s.scope_key}`;
    const children = childrenMap.get(nk) ?? [];
    const oc = outcomeCounts.get(nk) ?? 0;
    const display = manifestScopeDisplay(s);
    const decoded = decodeScopeKey(s.scope_key);
    const checked = selectedKeys?.has(nk) ?? true;

    return (
      <div key={nk}>
        <div
          style={{ paddingLeft: depth * 20 }}
          className={
            'flex items-center gap-2 py-1 px-1 rounded text-[11px] transition-colors ' +
            (selectable ? 'hover:border-neutral-600/40 cursor-pointer' : '')
          }
          onClick={selectable ? () => onToggleKey!(nk) : undefined}
        >
          {selectable && (
            <span
              className={
                'w-3.5 h-3.5 shrink-0 rounded border flex items-center justify-center transition-colors ' +
                (checked
                  ? 'bg-emerald-600 border-emerald-500'
                  : 'border-neutral-600 pac-glass')
              }
            >
              {checked && <Check className="w-2.5 h-2.5 text-white" />}
            </span>
          )}

          {depth > 0 && (
            <span className="text-neutral-700 text-[9px]">└</span>
          )}

          <span className="text-neutral-500 shrink-0">{s.scope_namespace}</span>
          <span className="text-neutral-700">/</span>

          <span
            className={
              'font-mono truncate ' +
              (checked
                ? 'text-neutral-200'
                : 'text-neutral-600 line-through')
            }
            title={s.scope_key}
          >
            {display}
          </span>

          {decoded && display !== decoded.label && (
            <span className="text-[9px] text-neutral-600 truncate">
              {decoded.label}
            </span>
          )}

          {oc > 0 && (
            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[8px] leading-none pac-glass-subtle text-neutral-400">
              {oc} outcomes
            </span>
          )}
        </div>
        {children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <div className="text-[11px] text-neutral-500">
        No scopes in manifest.
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {Array.from(groupedRoots.entries()).map(([ns, nsRoots]) => {
        const nsKeys = nsByNs.get(ns) ?? [];
        const allChecked = nsKeys.every(
          (k) => selectedKeys?.has(k) ?? true,
        );
        const noneChecked = nsKeys.every(
          (k) => !(selectedKeys?.has(k) ?? true),
        );
        const partial = !allChecked && !noneChecked;

        return (
          <div key={ns}>
            {selectable && groupedRoots.size > 1 && (
              <div
                className="flex items-center gap-2 py-1 px-1 border-b border-neutral-800/40 mb-0.5 cursor-pointer hover:border-neutral-600/30 rounded"
                onClick={() => onToggleNamespace?.(ns, !allChecked)}
              >
                <span
                  className={
                    'w-3.5 h-3.5 shrink-0 rounded border flex items-center justify-center transition-colors ' +
                    (allChecked
                      ? 'bg-emerald-600 border-emerald-500'
                      : partial
                        ? 'bg-neutral-700 border-neutral-500'
                        : 'border-neutral-600 pac-glass')
                  }
                >
                  {(allChecked || partial) && (
                    <Check className="w-2.5 h-2.5 text-white" />
                  )}
                </span>
                <span className="text-[10px] text-neutral-400 font-medium">
                  {ns}
                </span>
                <span className="text-[9px] text-neutral-600 tabular-nums">
                  {nsKeys.filter((k) => selectedKeys?.has(k)).length}/
                  {nsKeys.length}
                </span>
              </div>
            )}
            {nsRoots.map((r) => renderNode(r, 0))}
          </div>
        );
      })}
    </div>
  );
}
